import { defineComponent } from '@nuxtjs/composition-api';
import { gql } from '@apollo/client/core';
import JobsFireJobsHiringEventMutation from '@/apollo/mutations/Jobs/JobsFireJobsHiringEvent.mutation';
import RejectJobsHiringMutation from '@/apollo/mutations/Jobs/RejectJobsHiring.gql';
import type {
  FireJobsHiringEventMutation,
  FireJobsHiringEventMutationVariables,
  JobsInquiry,
  RejectJobsHiringMutation as RejectJobsHiringMutationType,
  RejectJobsHiringMutationVariables,
} from '@/types/graphql';
import { JobsHiringEventName } from '@/types/graphql';

const INQUIRY_FRAGMENT = gql`
  fragment JobsInquiry on JobsInquiry {
    id
    hiringsForReview {
      id
      employer {
        id
        descriptionHtml
        keyPoints
        roleUrl
        logoUrl
        company {
          id
          domain
          homepageUrl
          name
        }
      }
    }
  }
`;

export default defineComponent({
  name: 'JobsCandidateMutateMixin',
  data() {
    return {
      upserting: false,
    };
  },
  methods: {
    async fireHiringEvent(
      hiringId: string,
      eventName: JobsHiringEventName,
      withNotification: boolean = false,
    ): Promise<void> {
      if (this.upserting) return;

      try {
        this.upserting = true;
        await this.$apollo.mutate<FireJobsHiringEventMutation, FireJobsHiringEventMutationVariables>({
          mutation: JobsFireJobsHiringEventMutation,
          variables: {
            input: {
              id: hiringId,
              eventName,
            },
          },
        });

        if (withNotification) {
          this.$notice.success(
            eventName === JobsHiringEventName.RejectByCandidate ? 'Match has been rejected' : 'Match has been approved',
          );
        }

        this.upserting = false;
      } catch (e) {
        if (withNotification) {
          this.$notice.error(`Failed to fire the ${eventName} event: `, e as Error);
        }
        this.upserting = false;
        this.$sentryUtils.error(e);
        throw e;
      }
    },

    async rejectHiringWithReason(
      hiringId: string,
      rejectionReason: string,
      withNotification: boolean = false,
    ): Promise<void> {
      if (this.upserting) return;

      try {
        this.upserting = true;
        await this.$apollo.mutate<RejectJobsHiringMutationType, RejectJobsHiringMutationVariables>({
          mutation: RejectJobsHiringMutation,
          variables: {
            input: {
              id: hiringId,
              rejectionReason,
            },
          },
        });

        if (withNotification) {
          this.$notice.success('Match has been rejected');
        }

        this.upserting = false;
      } catch (e) {
        this.$notice.error(`Failed to reject the hiring: `, e as Error);
        this.upserting = false;
        this.$sentryUtils.error(e);
        throw e;
      }
    },

    changeCache(inquiry: JobsInquiry) {
      const client = this.$apolloProvider.defaultClient;

      client.writeFragment({
        id: `JobsInquiry:${inquiry.id}`,
        fragment: INQUIRY_FRAGMENT,
        data: inquiry,
      });
    },
  },
});
